import React, { useCallback, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { fetchRestaurantOrdersThunk, setRestaurantOrderStatusThunk, updateRestaurantOrderThunk } from '../../../../redux/thunk/restaurant/orders'
import ModalPaymentType from '../../areas/tables/modalPaymentType'
import { setRestaurantOrderChangeModal, setRestaurantPaymentTypeModal, setRestaurantShowOrderCancelModal, setRestaurantShowOrderDataModal, setRestaurantShowReceiptPayerData } from '../../../../redux/action/restaurant'
import ModalLogin from '../../../Elements/modalLogin'
import ButtonsModal from '../../../Elements/modal/ButtonsModal'
import { useTranslation } from 'react-i18next'
import { selectAccessToken, selectContainsAnyRole, selectHasAnyGroup } from '../../../../redux/selector/system'
import { getGiftCouponsPayment, getGiftCouponsSum } from '../../../../helper/getGiftCouponsPayment'
import { getPreparedItems } from './helper'
import { roundPrice } from '../../../../helper'
import { ENTERTAINMENT_EXPENSES_RECEIPT_MIN_SUM } from '../../../Elements/modal/PayerDataModal/constants'
import { printRequestsThunk } from '../../../../redux/thunk/order/printRequests'
import PayerDataModal from '../../../Elements/modal/PayerDataModal'
import OrderDataModal from '../../../Elements/modal/order/OrderDataModal'
import { getDepositsSum } from '../../../../helper/getDepositsPayment'
import { selectProcessedBy } from '../../../../redux/selector/restaurant'
import OrderCancelModal from './cancel'
import { useOpenOrderInvoice } from '../../../Elements/table/InvoicesTable/hooks/useOpenOrderInvoice'
import { useOrderInvoiceButtons } from '../../../Elements/table/InvoicesTable/hooks/useOrderInvoiceButtons'
import InvoiceCustomerDataModal from '../../../Elements/cart/InvoiceModals/InvoiceCustomerDataModal'
import CancelInvoiceOrderModal from '../../../Elements/modal/order/CancelInvoiceOrderModal'
import ChangePaymentConfirmModal from '../../../Elements/modal/order/ChangePaymentConfirmModal'
import { useCancellationWindow } from '../../../../hooks/useCancellationWindow'

const EVENT_Z_INDEX = 1

const ModalOrder = ({ selectedOrder, setSelectedOrder }) => {
	const { t } = useTranslation()
	const dispatch = useDispatch()
	const showModalOrder = useSelector(state => state.restaurant.orderChangeModal)
	const showReceiptPayerData = useSelector(state => state.restaurant.showReceiptPayerData)
	const showOrderDataModal = useSelector(state => state.restaurant.showOrderDataModal)
	const showOrderCancelModal = useSelector(state => state.restaurant.showOrderCancelModal)
	const [adminAccessToken, setAdminAccessToken] = useState(null)
	const [modalLoginIsOpen, setModalLoginIsOpen] = useState(false)
	const [showChangePaymentConfirm, setShowChangePaymentConfirm] = useState(false)
	const hasRoleChangeOrder = useSelector(selectContainsAnyRole(['ORDERS_WRITE']))
	const hasRoleCancelOrder = useSelector(selectHasAnyGroup(['OWNER', 'SUPERADMIN', 'ADMIN', 'CALL_AGENT']))
	const userAccessToken = useSelector(selectAccessToken)
	const extraDiscountOrderItem = selectedOrder?.items?.find(i => i.type === 'EXTRA_DISCOUNT')
	const extraDiscountPrice = extraDiscountOrderItem?.price || 0
	const extraDiscountPriceWithoutMinus = Math.abs(extraDiscountPrice)
	const totalPriceWithExtraDiscount = selectedOrder?.paymentMethod !== 'EC_TERMINAL' ? selectedOrder?.totalPrice + extraDiscountPriceWithoutMinus - selectedOrder?.tips : selectedOrder?.totalPrice + extraDiscountPriceWithoutMinus - selectedOrder?.tips
	const accessTokenForChangeOrder = hasRoleChangeOrder ? userAccessToken : adminAccessToken
	const isClosed = !!selectedOrder?.zNumber
	const isCanceledOrder = ['CANCELED', 'ABORTED'].includes(selectedOrder?.status?.status)
	const isInvoiceOrder = !!selectedOrder?.payments?.some(payment => payment.method === 'INVOICE')
	const showPayerDataModalForReceipt = roundPrice(selectedOrder?.totalPrice) >= ENTERTAINMENT_EXPENSES_RECEIPT_MIN_SUM
	const processedBy = useSelector(selectProcessedBy)
	const { canCancelOrder } = useCancellationWindow()

	useEffect(() => {
		if (selectedOrder) {
			dispatch(setRestaurantOrderChangeModal(true))
		}
	}, [selectedOrder]) // eslint-disable-line react-hooks/exhaustive-deps

	const handleClose = useCallback(() => {
		setSelectedOrder(null)
		dispatch(setRestaurantOrderChangeModal(false))
	}, [dispatch, setSelectedOrder])

	const { openOrderInvoice } = useOpenOrderInvoice(handleClose)
	const { goToInvoiceButton, createInvoiceButton, recipientModalProps } = useOrderInvoiceButtons({ order: selectedOrder, isOpen: !!selectedOrder && showModalOrder, openOrderInvoice, zIndex: EVENT_Z_INDEX })

	const handlePaymentChange = useCallback(() => {
		dispatch(setRestaurantPaymentTypeModal(true))
		dispatch(setRestaurantOrderChangeModal(false))
	}, [dispatch])

	const startPaymentChange = useCallback(() => {
		if (hasRoleChangeOrder) {
			handlePaymentChange()
		} else {
			setModalLoginIsOpen(true)
		}
	}, [hasRoleChangeOrder, handlePaymentChange])

	const handlePaymentChangeClick = useCallback(() => {
		if (isInvoiceOrder) {
			dispatch(setRestaurantOrderChangeModal(false))
			setShowChangePaymentConfirm(true)
		} else {
			startPaymentChange()
		}
	}, [dispatch, isInvoiceOrder, startPaymentChange])

	const closeChangePaymentConfirm = useCallback(() => {
		setShowChangePaymentConfirm(false)
		dispatch(setRestaurantOrderChangeModal(true))
	}, [dispatch])

	const confirmChangePayment = useCallback(() => {
		setShowChangePaymentConfirm(false)
		startPaymentChange()
	}, [startPaymentChange])

	const handleResetOrder = useCallback(async () => {
		dispatch(fetchRestaurantOrdersThunk(0, 20))
		setSelectedOrder(null)
	}, [dispatch, setSelectedOrder])

	const handlePaymentType = useCallback(({ paymentId, paymentMethod, totalDiscount, extraDiscountItem, tips = 0, giftCoupons = [], deposits = [] }) => {
		const isEcTerminalPaymentMethod = paymentMethod === 'EC_TERMINAL'
		const isTap2PayPaymentMethod = paymentMethod === 'TAP_TO_PAY'

		const totalPrice = (isEcTerminalPaymentMethod || isTap2PayPaymentMethod) ?
			roundPrice(totalPriceWithExtraDiscount - extraDiscountItem.value + tips) :
			roundPrice(totalPriceWithExtraDiscount - extraDiscountItem.value - tips)

		const giftCouponsPayment = getGiftCouponsPayment(giftCoupons)
		const giftCouponsSum = getGiftCouponsSum(giftCoupons)
		const depositsSum = getDepositsSum(deposits)
		const restSum = roundPrice(totalPrice - depositsSum - giftCouponsSum)
		const singlePayment = paymentMethod ? { id: paymentId, method: paymentMethod, total: restSum, payed: false } : null
		const payments = singlePayment ?
			[...deposits, ...giftCouponsPayment, singlePayment] :
			[...deposits, ...giftCouponsPayment]

		const items = getPreparedItems(selectedOrder, extraDiscountItem)

		dispatch(updateRestaurantOrderThunk(selectedOrder.orderId, {
			...selectedOrder, paymentMethod, totalPrice, totalDiscount, items, payments, tips, printerMetaData: { userPrintRequest: { userName: processedBy } },
		}, accessTokenForChangeOrder))
		if (!isEcTerminalPaymentMethod && !isTap2PayPaymentMethod) {
			handleResetOrder()
		}
	}, [totalPriceWithExtraDiscount, selectedOrder, accessTokenForChangeOrder, dispatch, handleResetOrder, processedBy])

	const handlePrint = async () => {
		await dispatch(printRequestsThunk(selectedOrder.orderId, 'CUSTOMER_RECEIPT_DUPLICATE', false, processedBy))
		await dispatch(printRequestsThunk(selectedOrder.orderId, 'KITCHEN_RECEIPT', null, processedBy))
		setSelectedOrder(null)
		dispatch(setRestaurantOrderChangeModal(false))
	}

	const handleCancel = useCallback(async () => {
		dispatch(setRestaurantShowOrderCancelModal(true))
		dispatch(setRestaurantOrderChangeModal(false))
	}, [dispatch])

	const handlePrintEntertainmentExpensesReceipt = async () => {
		if (showPayerDataModalForReceipt) {
			dispatch(setRestaurantShowReceiptPayerData(true))
		} else {
			await dispatch(printRequestsThunk(selectedOrder.orderId, 'ENTERTAINMENT_EXPENSES_RECEIPT', null, processedBy))
			setSelectedOrder(null)
		}
		dispatch(setRestaurantOrderChangeModal(false))
	}

	const onAdminLogin = (accessToken) => {
		setAdminAccessToken(accessToken)
		handlePaymentChange()
	}

	const handleModalLoginClose = () => {
		setModalLoginIsOpen(false)
		dispatch(setRestaurantOrderChangeModal(true))
	}

	const closeReceiptPayerDataModal = useCallback(() => {
		dispatch(setRestaurantShowReceiptPayerData(false))
		setSelectedOrder(null)
	}, [dispatch, setSelectedOrder])

	const showOrderDetails = useCallback(() => {
		dispatch(setRestaurantOrderChangeModal(false))
		dispatch(setRestaurantShowOrderDataModal(true))
	}, [dispatch])

	const closeOrderDataModal = useCallback(() => {
		dispatch(setRestaurantShowOrderDataModal(false))
		setSelectedOrder(null)
	}, [dispatch, setSelectedOrder])

	const closeOrderCancelModal = useCallback(() => {
		dispatch(setRestaurantShowOrderCancelModal(false))
		setSelectedOrder(null)
	}, [dispatch, setSelectedOrder])

	const closeCancelInvoiceOrderModal = useCallback(() => {
		dispatch(setRestaurantShowOrderCancelModal(false))
		dispatch(setRestaurantOrderChangeModal(true))
	}, [dispatch])

	const handleCancelInvoiceOrder = useCallback(async (reason, reasonCode) => {
		await dispatch(setRestaurantOrderStatusThunk(selectedOrder.orderId, 'CANCELED', reason, accessTokenForChangeOrder, reasonCode))
		closeOrderCancelModal()
	}, [dispatch, selectedOrder, accessTokenForChangeOrder, closeOrderCancelModal])

	const buttons = [
		{ icon: 'show', text: t('order.modal.show_order'), onClick: showOrderDetails, show: true },
		{ icon: 'change', text: t('order.modal.change_payment'), onClick: handlePaymentChangeClick, show: !isClosed && !isCanceledOrder && (!isInvoiceOrder || canCancelOrder(selectedOrder)) },
		{ icon: 'print', text: t('order.modal.print'), onClick: handlePrint, show: !isCanceledOrder },
		{ icon: 'cancel', text: t('order.modal.cancel_order'), onClick: handleCancel, show: hasRoleChangeOrder && (!isInvoiceOrder || hasRoleCancelOrder) && !isClosed && !isCanceledOrder && canCancelOrder(selectedOrder) },
		{ icon: 'check', text: t('order.modal.receipt'), onClick: handlePrintEntertainmentExpensesReceipt, show: !isCanceledOrder },
		goToInvoiceButton,
		createInvoiceButton,
	]

	return (
		<>
			<ButtonsModal isOpen={!!selectedOrder && showModalOrder}
										id="modal-order"
										close={handleClose}
										title={`${t('Modal.order')} #` + selectedOrder?.orderDayCounter}
										titlePrice={selectedOrder?.totalPrice}
										zIndex={EVENT_Z_INDEX}
										autoFocus={false}
										buttons={buttons} />
			<InvoiceCustomerDataModal {...recipientModalProps} />
			{selectedOrder &&
				<ModalLogin isOpen={modalLoginIsOpen}
										close={handleModalLoginClose}
										group="SUPERADMIN"
										title={t('Login.LoginRestaurantAdmin')}
										onLogin={onAdminLogin}
										storeAccessToken={false}
										returnZIndex={0} />}
			{selectedOrder &&
				<ModalPaymentType totalPrice={totalPriceWithExtraDiscount}
													extraDiscountOrderItem={extraDiscountOrderItem}
													title={'Bestellung Nr. ' + selectedOrder?.orderDayCounter}
													totalDiscount={selectedOrder?.totalDiscount}
													handlePaymentType={handlePaymentType}
													updatePaymentMode={true}
													resetTable={handleResetOrder} />}
			<PayerDataModal isOpen={!!selectedOrder && showReceiptPayerData}
											close={closeReceiptPayerDataModal}
											order={selectedOrder} />

			<OrderDataModal modalIsOpen={!!selectedOrder && showOrderDataModal}
											order={selectedOrder}
											close={closeOrderDataModal} />
			<OrderCancelModal isOpen={!!selectedOrder && showOrderCancelModal && !isInvoiceOrder}
												close={closeOrderCancelModal}
												order={selectedOrder}
												accessToken={accessTokenForChangeOrder} />
			<CancelInvoiceOrderModal isOpen={!!selectedOrder && showOrderCancelModal && isInvoiceOrder}
															 close={closeCancelInvoiceOrderModal}
															 onConfirm={handleCancelInvoiceOrder}
															 order={selectedOrder} />
			<ChangePaymentConfirmModal isOpen={!!selectedOrder && showChangePaymentConfirm}
																 close={closeChangePaymentConfirm}
																 onConfirm={confirmChangePayment}
																 order={selectedOrder} />
		</>
	)
}

ModalOrder.displayName = 'ModalOrder'
export default ModalOrder
